let PickupUtil = function() {
    'use strict';
    const SERVICE_GROUP = 'SERVICE_GROUP',
        PICKUPID = 'PICKUPID',
        CARRIER_NAME = 'CARR_SRV_NAME',
        NAME = 'NAME1',
        STREET = 'STREET',
        POSTALCODE = 'PSTLZ',
        CITY = 'ORT01',
        OPENHRS = 'OPENHRS',
        LATITUDE = 'LATITUDE',
        LONGITUDE = 'LONGITUDE',
        DISTANCE = 'DISTANCE',
        LOCKER = 'SRCODE',
        UNIT = 'UNIT',
        CARRIER_CODE = 'CARRIER_CODE';

    /**
     * Pickup Point shipping methods.
     * Values are shipping method codes.
     */
    const shippingMethods = {
        UPS: 'K1',
        BRING: 'K2',
        METAPACK: 'M3'
    };

    /**
     * Returns `true` if the given shipping method is a Pickup Point method, `false` otherwise.
     * @param {string} code Shipping method code
     * @returns {boolean}
     */
    function isPickupPointShippingMethod(code) {
        return Object.values(shippingMethods).includes(code);
    }

    /**
     * Parses the given Custom fields to create a list of Pickup Points.
     * @param {CustomField[]} customData Custom Fields from SAP Sales Order Detail object
     * @returns {PickupPoint[]} List of Pickup Points
     */
    function parsePickupPoints(customData) {
        let pickupPoints = [];

        const serviceGroups = customData.filter(item => item.Key === SERVICE_GROUP);
        const ids = customData.filter(item => item.Key === PICKUPID);
        const carriers = customData.filter(item => item.Key === CARRIER_NAME);
        const names = customData.filter(item => item.Key === NAME);
        const streets = customData.filter(item => item.Key === STREET);
        const cities = customData.filter(item => item.Key === CITY);
        const postalCodes = customData.filter(item => item.Key === POSTALCODE);
        const distances = customData.filter(item => item.Key === DISTANCE);
        const lockers = customData.filter(item => item.Key === LOCKER);
        const units = customData.filter(item => item.Key === UNIT);
        const openHours = customData.filter(item => item.Key === OPENHRS);
        const latitudes = customData.filter(item => item.Key === LATITUDE);
        const longitudes = customData.filter(item => item.Key === LONGITUDE);
        const carrierCodes = customData.filter(item => item.Key === CARRIER_CODE);

        for (let i = 0; i < ids.length; i++) {
            pickupPoints.push({
                serviceGroup: serviceGroups && serviceGroups[i] ? serviceGroups[i].Value : undefined,
                id: ids[i].Value,
                carrier: carriers && carriers[i] ? carriers[i].Value : undefined,
                name: names && names[i] ? names[i].Value : undefined,
                street: streets && streets[i] ? streets[i].Value : undefined,
                city: cities && cities[i] ? cities[i].Value : undefined,
                postalCode: postalCodes && postalCodes[i] ? postalCodes[i].Value : undefined,
                distance: distances && distances[i] ? distances[i].Value : undefined,
                locker: lockers && lockers[i] ? lockers[i].Value : undefined,
                unit: units && units[i] ? units[i].Value : undefined,
                openingHours: openHours && openHours[i] ? openHours[i].Value.split(/\s*;\s*/) : undefined,
                latitude: latitudes && latitudes[i] ? parseFloat(latitudes[i].Value) : undefined,
                longitude: longitudes && longitudes[i] ? parseFloat(longitudes[i].Value) : undefined,
                carrierCode: carrierCodes && carrierCodes[i] ? carrierCodes[i].Value : undefined
            });
        }
        pickupPoints.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
        pickupPoints.forEach((point, idx) => point.icon = `https://chart.apis.google.com/chart?chst=d_map_pin_letter&chld=${idx + 1}|008AB0|FFFFFF`);

        return pickupPoints;
    }

    /**
     * Creates a Google Maps LatLngBounds instance that encloses the given GPS coordinates.
     *
     * IMPORTANT NOTE: this function expects the Google Maps API to be loaded.
     * @param {{latitude: number, longitude: number}[]} points List of GPS coordinates to enclose
     * @returns {google.maps.LatLngBounds} Bounding box containing the given points
     */
    function getBounds(points) {
        if (!Array.isArray(points)) {
            throw 'No points supplied; cannot calculate center';
        }

        return points.reduce((bounds, point) => {
            return bounds.extend({
                lat: point.latitude,
                lng: point.longitude
            });
        }, new google.maps.LatLngBounds()); // eslint-disable-line
    }

    /**
     * Creates a list of map marker data objects for the given Pickup Points.
     * @param {PickupPoint[]} points List of Pickup Points
     * @returns {PickupPointMarker[]}
     */
    function generateMarkers(points) {
        if (!Array.isArray(points)) {
            throw 'No points supplied; cannot generate markers';
        }

        let markers = points.map((point, index) => {
            return {
                id: point.id,
                index: index,
                icon: point.icon,
                name: point.name,
                street: point.street,
                postalCode: point.postalCode,
                city: point.city,
                carrier: point.carrier,
                distance: point.distance,
                position: [point.latitude, point.longitude],
                openingHours: point.openingHours,
                locker: point.locker
            };
        });
        return markers;
    }

    return {
        allMethods: shippingMethods,
        supports: isPickupPointShippingMethod,
        parse: parsePickupPoints,
        bounds: getBounds,
        markers: generateMarkers
    }
}();

export default PickupUtil;

/**
 * Object representing a custom data field used in communication with Nu Skin Shop APIs.
 * This is used to pass a data value from client to server or vice-versa, where the value
 * in question doesn't fit into the existing API data fields.
 * @typedef CustomField
 * @property {string} Type - Custom field type
 * @property {string} Key - Custom field key name
 * @property {string} Value - Custom field value
 */

/**
 * Object representing a Pickup Point location.
 * @typedef PickupPoint
 * @property {string} id - ID of the Pickup Point location
 * @property {string} name - Name of the Pickup Point location
 * @property {string} [locker] - Locker number (if applicable)
 * @property {string} street - Street address
 * @property {string} city - City
 * @property {string} postalCode - Postal/Zip code
 * @property {string[]} openingHours - List of opening hours for the Pickup Point location
 * @property {string} distance - Distance of the Pickup Point from the reference address
 * @property {string} unit - Unit of measurement for the distance figure
 * @property {number} latitude - Latitude of the Pickup Point location
 * @property {number} longitude - Longitude of the Pickup Point location
 * @property {string} icon - Marker icon URL
 */

/**
 * Pickup Point map marker data.
 * @typedef PickupPointMarker
 * @property {number} index - Index of the Pickup Point in the list
 * @property {string} icon - Marker icon URL
 * @property {string} id - ID of the Pickup Point location
 * @property {string} name - Name of the Pickup Point location
 * @property {string} [locker] - Locker number (if applicable)
 * @property {string} street - Street address
 * @property {string} city - City
 * @property {string} postalCode - Postal/Zip code
 * @property {string[]} openingHours - List of opening hours for the Pickup Point location
 * @property {string} distance - Distance of the Pickup Point from the reference address
 * @property {number[]} position - Latitude-longitude pair
 */
